/**
 * User Achievements Transformer
 * Transforms BE UserAchievementsView to FE Achievement[] format
 */

import {
  AchievementTier,
  AchievementType as BEAchievementType,
  type AchievementStanding,
  type UserAchievementsView
} from 'pure-js/types/Achievement'
import {
  Achievement,
  AchievementId,
  AchievementLevel,
  AchievementThresholds,
  AchievementType,
  TieredAchievement
} from '../types/achievementTypes'
import { getAchievementText } from '../constants/achievementTextMapping'

/** The badge level shown for each tier. */
const ACHIEVEMENT_LEVELS_BY_TIER: Record<AchievementTier, AchievementLevel> = {
  [AchievementTier.BRONZE]: AchievementLevel.BRONZE,
  [AchievementTier.SILVER]: AchievementLevel.SILVER,
  [AchievementTier.GOLD]: AchievementLevel.GOLD
}

const toAchievementLevel = (tier: AchievementTier | null): AchievementLevel | undefined =>
  tier ? ACHIEVEMENT_LEVELS_BY_TIER[tier] : undefined

/** The thresholds as the badges read them; platinum is the same as gold. */
const toLevelThresholds = (thresholds: AchievementStanding['thresholds']): AchievementThresholds => ({
  Bronze: thresholds.bronze,
  Silver: thresholds.silver,
  Gold: thresholds.gold,
  Platinum: thresholds.gold
})

/**
 * Transform UserAchievementsView (BE) to Achievement[] (FE), each from where the person stands on it
 */
export function transformUserAchievementsToAchievements(userAchievements: UserAchievementsView): Achievement[] {
  // An achievement the answer gives no standing for is left out rather than guessed
  const standings: Partial<UserAchievementsView['standings']> = userAchievements.standings ?? {}

  return Object.values(BEAchievementType).flatMap((beType): TieredAchievement[] => {
    const standing = standings[beType]
    if (!standing) return []

    const textMapping = getAchievementText(beType)

    const achievement: TieredAchievement = {
      // The FE id list has no entry for these, so the BE type itself is the id
      id: beType as unknown as AchievementId,
      name: textMapping.title,
      description: textMapping.description,
      type: AchievementType.TIERED,
      currentValue: standing.value,
      thresholds: toLevelThresholds(standing.thresholds),
      level: toAchievementLevel(standing.currentTier),
      nextLevel: toAchievementLevel(standing.nextTier),
      progressPercent: standing.progressPercent,
      tooltip: textMapping.tooltip
    }

    return [achievement]
  })
}

/**
 * Get tooltip text for an achievement
 */
export function getAchievementTooltip(beType: BEAchievementType): string | undefined {
  const textMapping = getAchievementText(beType)
  return textMapping.tooltip
}
